import { User, Group, Role, RoleBinding, RoleBindingRecord, IAMPolicyBinding, ResourceBinding, DirectoryUsersResponse, UserInvite, CreateInviteResponse } from '../types/iam';
import api from './api';

// ===== Users =====

export const getUsers = async (params?: { status?: string; q?: string; limit?: number; offset?: number }) => {
  const response = await api.get<{ data: User[]; total: number }>(`/iam/users`, { params });
  return response?.data || { data: [], total: 0 };
};

// getDirectoryUsers proxies the external d2bsuite directory (admin-only on the
// backend) to populate the Add-user email dropdown.
export const getDirectoryUsers = async (params?: {
  search?: string;
  pageNo?: number;
  pageSize?: number;
  sortBy?: string;
  direction?: string;
}) => {
  const response = await api.get<DirectoryUsersResponse>(`/iam/directory/users`, { params });
  return response.data;
};

// createUser provisions a directory user into this tenant with the chosen role.
export const createUser = async (data: {
  email: string;
  name?: string;
  primary_role: string;
  external_id?: string;
  picture_url?: string;
}) => {
  const response = await api.post<User>(`/iam/users`, data);
  return response.data;
};

export const getUser = async (id: string) => {
  const response = await api.get<User>(`/iam/users/${id}`);
  return response.data;
};

export const updateUserStatus = async (id: string, status: string) => {
  await api.patch(`/iam/users/${id}`, { status });
};

export const updateUser = async (id: string, data: { primary_role?: string }) => {
  const response = await api.patch<User>(`/iam/users/${id}`, data);
  return response.data;
};

export const deleteUser = async (id: string) => {
  await api.delete(`/iam/users/${id}`);
};

export const getUserGroups = async (id: string) => {
  const response = await api.get<{ data: Group[] }>(`/iam/users/${id}/groups`);
  return response.data;
};

export const getUserRoleBindings = async (id: string) => {
  const response = await api.get<{ data: RoleBindingRecord[] }>(`/iam/users/${id}/role-bindings`);
  return response.data;
};

// ===== Invites =====
// Admin-only. An invite admits one email to this workspace with the given roles
// and groups when that person first signs in (verified email); until then it is
// pending, and it lapses at expires_at.

export const getInvites = async (params?: { status?: string; limit?: number; offset?: number }) => {
  const response = await api.get<{ data: UserInvite[]; total: number }>(`/iam/invites`, { params });
  return response?.data || { data: [], total: 0 };
};

export const createInvite = async (data: {
  email: string;
  roles?: string[];
  groups?: string[];
  expires_in_hours?: number;
  send_email?: boolean;
}) => {
  const response = await api.post<CreateInviteResponse>(`/iam/invites`, data);
  return response.data;
};

export const revokeInvite = async (id: string) => {
  await api.delete(`/iam/invites/${id}`);
};

// List endpoints answer { data, total }. Some backends send the array bare, or
// under the resource name ({ groups: [...] }); read all three, so a list never
// renders empty just because of the envelope.
function asList<T>(body: unknown, key: string): { data: T[]; total: number } {
  if (Array.isArray(body)) return { data: body as T[], total: body.length };
  const b = (body ?? {}) as Record<string, unknown>;
  const rows = (Array.isArray(b.data) ? b.data : Array.isArray(b[key]) ? b[key] : []) as T[];
  return { data: rows, total: typeof b.total === 'number' ? b.total : rows.length };
}

// ===== Groups =====

export const getGroups = async (params?: { q?: string; limit?: number; offset?: number }) => {
  const response = await api.get(`/iam/groups`, { params });
  return asList<Group>(response?.data, 'groups');
};

export const getGroup = async (id: string) => {
  const response = await api.get<Group>(`/iam/groups/${id}`);
  return response.data;
};

export const updateGroup = async (id: string, group: Partial<Group>) => {
  const response = await api.put<Group>(`/iam/groups/${id}`, group);
  return response.data;
};

export const deleteGroup = async (id: string) => {
  await api.delete(`/iam/groups/${id}`);
};

export const createGroup = async (group: Partial<Group>) => {
  const response = await api.post<Group>(`/iam/groups`, group);
  return response.data;
};

export const getGroupMembers = async (id: string, params?: { limit?: number; offset?: number }) => {
  const response = await api.get<{ data: User[]; total: number }>(`/iam/groups/${id}/members`, { params });
  return response?.data || { data: [], total: 0 };
};

export const addGroupMember = async (groupId: string, userId: string) => {
  await api.post(`/iam/groups/${groupId}/members`, { user_id: userId });
};

export const removeGroupMember = async (groupId: string, userId: string) => {
  await api.delete(`/iam/groups/${groupId}/members/${userId}`);
};

export const getGroupRoleBindings = async (groupId: string) => {
  const response = await api.get<{ data: RoleBindingRecord[] }>(`/iam/groups/${groupId}/role-bindings`);
  return response?.data?.data || [];
};

// ===== Roles =====

export const getRoles = async (params?: { q?: string; limit?: number; offset?: number; primary_only?: boolean }) => {
  const response = await api.get(`/iam/roles`, { params });
  return asList<Role>(response?.data, 'roles');
};

export const getRole = async (id: string) => {
  const response = await api.get<Role>(`/iam/roles/${id}`);
  return response.data;
};

export const createRole = async (role: Partial<Role>) => {
  const response = await api.post<Role>(`/iam/roles`, role);
  return response.data;
};

export const updateRole = async (id: string, role: Partial<Role>) => {
  const response = await api.put<Role>(`/iam/roles/${id}`, role);
  return response.data;
};

export const deleteRole = async (id: string) => {
  await api.delete(`/iam/roles/${id}`);
};

export const getRolePolicyBindings = async (roleId: string) => {
  const response = await api.get<{ data: IAMPolicyBinding[] }>(`/iam/roles/${roleId}/policy-bindings`);
  return response.data;
};

export const deletePolicyBinding = async (id: string) => {
  await api.delete(`/iam/policy-bindings/${id}`);
};

export const bulkUpdatePolicyBindings = async (roleId: string, data: { add: string[]; remove: string[] }) => {
  const response = await api.put(`/iam/roles/${roleId}/policy-bindings/bulk`, data);
  return response.data;
};

// ===== Role Bindings =====

export const createRoleBinding = async (binding: RoleBinding) => {
  const response = await api.post<RoleBindingRecord>(`/iam/role-bindings`, binding);
  return response.data;
};

export const deleteRoleBinding = async (id: string) => {
  await api.delete(`/iam/role-bindings/${id}`);
};

// ===== Resource Bindings =====

export const getRoleResourceBindings = async (roleId: string) => {
  const response = await api.get<{ data: ResourceBinding[] }>(`/iam/roles/${roleId}/resource-bindings`);
  return response.data;
};

export const bulkUpdateResourceBindings = async (roleId: string, data: { add: ResourceBinding[]; remove: string[] }) => {
  const response = await api.put(`/iam/roles/${roleId}/resource-bindings/bulk`, data);
  return response.data;
};


